import React, { useState, useEffect, useRef } from 'react';
import './TermsAndPrivacyModal.css';

/**
 * TermsAndPrivacyModal
 * Self-contained Terms & Privacy modal for ImSelling.
 *
 * DROP-IN USAGE — add 3 lines to your signup/auth component:
 *
 *   import TermsAndPrivacyModal from './TermsAndPrivacyModal';
 *
 *   const [showTerms, setShowTerms] = useState(false);
 *
 *   // Replace your existing Terms link with:
 *   <span className="terms-link" onClick={() => setShowTerms(true)}>Terms & Privacy</span>
 *
 *   // Add the modal anywhere inside your return():
 *   <TermsAndPrivacyModal isOpen={showTerms} onClose={() => setShowTerms(false)} />
 */
const TermsAndPrivacyModal = ({ isOpen, onClose, onAccept }) => {
  const [activeSection, setActiveSection] = useState('terms');
  const contentRef = useRef(null);

  // Scroll content to top whenever section changes
  useEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [activeSection]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // Prevent body scroll while open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="tp-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label="Terms and Privacy">
      <div className="tp-modal" onClick={(e) => e.stopPropagation()}>

        {/* ── Header ───────────────────────────────────────────── */}
        <div className="tp-header">
          <div className="tp-header-brand">
            <span className="tp-brand-icon">🛒</span>
            <span className="tp-brand-name">ImSelling</span>
          </div>
          <button className="tp-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {/* ── Tab switcher ─────────────────────────────────────── */}
        <div className="tp-tabs">
          <button
            className={`tp-tab ${activeSection === 'terms' ? 'tp-tab-active' : ''}`}
            onClick={() => setActiveSection('terms')}
          >
            📋 Terms of Service
          </button>
          <button
            className={`tp-tab ${activeSection === 'privacy' ? 'tp-tab-active' : ''}`}
            onClick={() => setActiveSection('privacy')}
          >
            🔒 Privacy Policy
          </button>
          <button
            className={`tp-tab ${activeSection === 'safety' ? 'tp-tab-active' : ''}`}
            onClick={() => setActiveSection('safety')}
          >
            🛡️ Safety & Reporting
          </button>
        </div>

        {/* ── Scrollable content ───────────────────────────────── */}
        <div className="tp-content" ref={contentRef}>

          {/* ══════════════════ TERMS OF SERVICE ══════════════════ */}
          {activeSection === 'terms' && (
            <div className="tp-section">
              <div className="tp-hero">
                <h1 className="tp-hero-title">Terms of Service</h1>
                <p className="tp-hero-sub">
                  Effective Date: January 1, 2025 &nbsp;·&nbsp; ImSelling, Kenya
                </p>
              </div>

              <div className="tp-notice tp-notice-warn">
                <strong>⚠️ Please read carefully.</strong> By creating an account or using ImSelling,
                you confirm that you have read, understood, and agreed to these Terms of Service in full.
                If you do not agree, please do not use this platform.
              </div>

              <Section title="1. About ImSelling">
                <p>
                  ImSelling is an online classifieds and marketplace platform operating in Kenya,
                  connecting buyers and sellers of goods and services. We act solely as an
                  intermediary platform — we do <strong>not</strong> buy, sell, own, or guarantee
                  any item listed on the platform.
                </p>
                <p>
                  ImSelling is available to Kenyan residents and businesses aged 18 years and above,
                  or minors acting under verified parental/guardian supervision.
                </p>
              </Section>

              <Section title="2. Eligibility & Account Registration">
                <ul>
                  <li>You must provide accurate, truthful, and complete registration information.</li>
                  <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
                  <li>One person or business entity may hold only one active account unless explicitly authorised by ImSelling.</li>
                  <li>Accounts created using bots, scripts, or automated tools are strictly prohibited.</li>
                  <li>We reserve the right to suspend or permanently ban accounts at our sole discretion for violations of these Terms.</li>
                </ul>
              </Section>

              <Section title="3. Listings & Permitted Content">
                <p>
                  ImSelling is designed for the <strong>marketing and sale of legitimate physical and digital
                  goods and services</strong> that are consistent with the categories featured on our platform.
                  This includes but is not limited to electronics, clothing, furniture, vehicles, real estate,
                  services, and locally manufactured or imported goods.
                </p>
                <p><strong>You may NOT list or advertise:</strong></p>
                <ul>
                  <li>Counterfeit, pirated, or stolen goods</li>
                  <li>Illegal substances, narcotics, or controlled goods without proper licencing</li>
                  <li>Weapons, firearms, ammunition, or explosives</li>
                  <li>Endangered animals or animal parts prohibited under Kenyan law or CITES</li>
                  <li>Adult/pornographic content of any kind</li>
                  <li>Human trafficking, exploitation, or services of a sexual nature</li>
                  <li>Financial scam products, pyramid schemes, or Ponzi investments</li>
                  <li>Fake currency, forged documents, or fraudulent financial instruments</li>
                  <li>Items that are off-topic or unrelated to the categories featured on our platform</li>
                  <li>Services that impersonate another person, business, or government entity</li>
                  <li>Any item or service whose sale is prohibited under Kenyan law</li>
                </ul>
                <p>
                  Listings that violate these rules will be removed without notice, and the account may be
                  permanently banned. ImSelling cooperates fully with Kenyan law enforcement agencies.
                </p>
              </Section>

              <Section title="4. Seller Responsibilities">
                <ul>
                  <li>Sellers must accurately describe items — price, condition, location, and images must be truthful.</li>
                  <li>Sellers must honour agreed prices and complete transactions in good faith.</li>
                  <li>Sellers must not engage in bait-and-switch tactics or price manipulation after initial contact.</li>
                  <li>Sellers are solely responsible for the quality, legality, and delivery of their goods/services.</li>
                  <li>Sellers must respond to buyer enquiries within a reasonable timeframe.</li>
                  <li>Commercial sellers operating as businesses must comply with Kenyan consumer protection laws,
                      including the Consumer Protection Act, 2012.</li>
                </ul>
              </Section>

              <Section title="5. Buyer Responsibilities">
                <ul>
                  <li>Buyers must conduct due diligence before making any purchase or payment.</li>
                  <li>Buyers should inspect goods thoroughly before completing a transaction.</li>
                  <li>Buyers must not file false reports or make fraudulent claims against sellers.</li>
                  <li>Buyers acknowledge that ImSelling does not guarantee the quality, safety,
                      or legality of items listed by third-party sellers.</li>
                </ul>
              </Section>

              <Section title="6. Fees & Premium Plans">
                <ul>
                  <li>Posting a Free-tier ad is completely free.</li>
                  <li>Premium plans (Basic, Pro, Business) require payment via M-Pesa before activation.</li>
                  <li>All fees are non-refundable once a plan is activated and an ad is live, unless otherwise stated.</li>
                  <li>ImSelling reserves the right to change pricing at any time with reasonable notice.</li>
                  <li>Promotional slots granted by ImSelling are non-transferable and expire as indicated.</li>
                </ul>
              </Section>

              <Section title="7. Prohibited Conduct">
                <p>The following are strictly forbidden on ImSelling:</p>
                <ul>
                  <li><strong>Impersonation:</strong> Pretending to be another seller, buyer, or ImSelling staff.</li>
                  <li><strong>Spam:</strong> Posting duplicate or irrelevant listings; unsolicited bulk messaging.</li>
                  <li><strong>Manipulation:</strong> Artificially inflating listing views, reviews, or engagement.</li>
                  <li><strong>Scraping:</strong> Automated data extraction from the platform without written permission.</li>
                  <li><strong>Harassment:</strong> Abusive, threatening, or discriminatory communication to any user.</li>
                  <li><strong>Fraud:</strong> Any deceptive practice intended to mislead buyers or sellers.</li>
                  <li><strong>Account sharing:</strong> Sharing login credentials with third parties.</li>
                </ul>
              </Section>

              <Section title="8. Limitation of Liability">
                <div className="tp-notice tp-notice-info">
                  <strong>ImSelling is a marketplace platform, not a party to any transaction.</strong>
                </div>
                <ul>
                  <li>ImSelling is <strong>not liable</strong> for any loss, damage, injury, or financial harm
                      arising from transactions between buyers and sellers on this platform.</li>
                  <li>We are <strong>not liable</strong> for misrepresentation, fraud, or misconduct by any user.</li>
                  <li>We are <strong>not liable</strong> for loss of data, business interruption, or technical errors.</li>
                  <li>We are <strong>not liable</strong> for any advance payments made by buyers without conducting
                      proper due diligence.</li>
                  <li>While we strive to ensure genuine businesses are listed, we <strong>cannot guarantee</strong> the
                      authenticity of every seller. Users transact at their own risk.</li>
                  <li>ImSelling's total liability, in any event, shall not exceed the amount paid by the user
                      for the specific plan or service giving rise to the claim.</li>
                </ul>
              </Section>

              <Section title="9. Personalisation & Account Banning">
                <div className="tp-notice tp-notice-danger">
                  <strong>⚠️ Zero Tolerance:</strong> Impersonation and account manipulation will result in
                  permanent banning and potential legal action.
                </div>
                <ul>
                  <li>Personalisation of accounts to mislead users — including false business names, fake
                      verification badges, or impersonating well-known brands — is strictly prohibited.</li>
                  <li>Accounts found engaging in personalisation fraud will be <strong>permanently banned</strong>
                      without refund of any fees paid.</li>
                  <li>ImSelling reserves the right to pursue <strong>civil and criminal legal action</strong>
                      under the Computer Misuse and Cybercrimes Act, 2018 and other applicable Kenyan laws.</li>
                  <li>Banned users may not create new accounts. Circumventing a ban is itself a violation
                      subject to further legal action.</li>
                </ul>
              </Section>

              <Section title="10. Intellectual Property">
                <ul>
                  <li>All content on ImSelling (logo, branding, software, UI) is owned by ImSelling
                      and may not be copied, reproduced, or used without written permission.</li>
                  <li>By posting listings, you grant ImSelling a non-exclusive, royalty-free licence to
                      display your content on the platform for the purposes of operating the marketplace.</li>
                  <li>You must have the rights to all content (images, text, videos) you upload. Uploading
                      copyrighted material without permission is prohibited.</li>
                </ul>
              </Section>

              <Section title="11. Termination">
                <p>
                  ImSelling may suspend or terminate your account at any time, with or without notice,
                  for conduct that we believe violates these Terms or is harmful to other users, third parties,
                  or the business interests of ImSelling. Upon termination, your right to use the
                  platform immediately ceases.
                </p>
              </Section>

              <Section title="12. Governing Law">
                <p>
                  These Terms are governed by and construed in accordance with the laws of the
                  Republic of Kenya. Any disputes arising from these Terms or your use of the platform
                  shall be subject to the exclusive jurisdiction of the Kenyan courts.
                </p>
              </Section>

              <Section title="13. Changes to These Terms">
                <p>
                  ImSelling reserves the right to update these Terms at any time. Continued use of
                  the platform after changes are posted constitutes your acceptance of the revised Terms.
                  We will notify users of material changes via email or platform notification.
                </p>
              </Section>
            </div>
          )}

          {/* ══════════════════ PRIVACY POLICY ══════════════════ */}
          {activeSection === 'privacy' && (
            <div className="tp-section">
              <div className="tp-hero">
                <h1 className="tp-hero-title">Privacy Policy</h1>
                <p className="tp-hero-sub">
                  Effective Date: January 1, 2025 &nbsp;·&nbsp; ImSelling, Kenya
                </p>
              </div>

              <div className="tp-notice tp-notice-info">
                Your privacy matters to us. This Policy explains what data we collect, why we collect it,
                and how we protect it.
              </div>

              <Section title="1. Information We Collect">
                <p><strong>Registration data:</strong> Name, email address, phone number, password (encrypted).</p>
                <p><strong>Listing data:</strong> Photos, videos, item descriptions, prices, and location data you provide when posting ads.</p>
                <p><strong>Transaction data:</strong> M-Pesa payment references, plan subscriptions, and promo slot usage.</p>
                <p><strong>Usage data:</strong> Pages visited, search queries, click behaviour, device type, IP address, and browser information.</p>
                <p><strong>Communication data:</strong> Messages exchanged between buyers and sellers on the platform.</p>
                <p><strong>Verification data:</strong> ID documents submitted for seller verification (stored securely, never shared publicly).</p>
              </Section>

              <Section title="2. How We Use Your Data">
                <ul>
                  <li>To create and manage your account.</li>
                  <li>To display your listings to potential buyers.</li>
                  <li>To process payments and activate premium plans.</li>
                  <li>To send transactional notifications (account activity, payment confirmations).</li>
                  <li>To verify seller identities and improve platform safety.</li>
                  <li>To detect, prevent, and respond to fraud, abuse, or illegal activity.</li>
                  <li>To improve platform features, performance, and user experience.</li>
                  <li>To comply with our legal obligations under Kenyan law.</li>
                </ul>
              </Section>

              <Section title="3. Data Sharing">
                <p>We do <strong>not</strong> sell your personal data. We may share data with:</p>
                <ul>
                  <li><strong>Payment processors:</strong> Safaricom M-Pesa for payment processing only.</li>
                  <li><strong>Law enforcement:</strong> When required by Kenyan law or a valid court order.</li>
                  <li><strong>Service providers:</strong> Trusted third parties who help operate the platform
                      (e.g. cloud hosting, analytics) — bound by strict data processing agreements.</li>
                  <li><strong>Other users:</strong> Your public listing information (name, listing details,
                      general location) is visible to all platform users.</li>
                </ul>
              </Section>

              <Section title="4. Data Security">
                <ul>
                  <li>Passwords are stored using strong encryption (hashed — never in plain text).</li>
                  <li>All data is transmitted over encrypted HTTPS connections.</li>
                  <li>ID verification documents are stored in restricted-access secure storage.</li>
                  <li>We regularly review our security practices and update them as needed.</li>
                  <li>Despite best efforts, no system is 100% secure. Users are encouraged to use
                      strong passwords and report suspicious account activity immediately.</li>
                </ul>
              </Section>

              <Section title="5. Data Retention">
                <p>
                  We retain your account data for as long as your account is active. If you delete your
                  account, we will delete or anonymise your personal data within 30 days, except where
                  retention is required by Kenyan law (e.g. financial records, fraud investigations).
                </p>
              </Section>

              <Section title="6. Your Rights">
                <p>Under applicable Kenyan data protection law (Data Protection Act, 2019), you have the right to:</p>
                <ul>
                  <li>Access the personal data we hold about you.</li>
                  <li>Correct inaccurate or incomplete data.</li>
                  <li>Request deletion of your data (subject to legal retention requirements).</li>
                  <li>Object to processing of your data for direct marketing.</li>
                  <li>Lodge a complaint with the Office of the Data Protection Commissioner (ODPC) of Kenya.</li>
                </ul>
                <p>To exercise your rights, contact us at: <strong>support@imselling.co.ke</strong></p>
              </Section>

              <Section title="7. Cookies & Tracking">
                <p>
                  ImSelling uses cookies and similar tracking technologies to maintain your session,
                  remember preferences, and analyse usage patterns. You may disable cookies in your browser
                  settings, but this may affect platform functionality.
                </p>
              </Section>

              <Section title="8. Children's Privacy">
                <p>
                  ImSelling is not intended for users under the age of 18. We do not knowingly collect
                  personal data from children. If we become aware that a child has registered without
                  parental consent, we will delete the account immediately.
                </p>
              </Section>

              <Section title="9. Changes to This Policy">
                <p>
                  We may update this Privacy Policy periodically. Significant changes will be communicated
                  via email or platform notification. Continued use of the platform after changes
                  constitutes acceptance of the updated Policy.
                </p>
              </Section>
            </div>
          )}

          {/* ══════════════════ SAFETY & REPORTING ══════════════════ */}
          {activeSection === 'safety' && (
            <div className="tp-section">
              <div className="tp-hero">
                <h1 className="tp-hero-title">Safety & Reporting</h1>
                <p className="tp-hero-sub">
                  Staying safe on ImSelling — tips, guidelines, and how to report
                </p>
              </div>

              <div className="tp-notice tp-notice-success">
                <strong>🛡️ Our commitment:</strong> ImSelling is committed to maintaining a safe,
                trustworthy marketplace. We will take immediate action on all verified reports of
                suspicious or fraudulent activity.
              </div>

              {/* Safety Tips */}
              <div className="tp-safety-grid">
                <SafetyCard
                  icon="🤝"
                  title="Meet in Public Places"
                  desc="Always arrange to meet buyers or sellers in a busy, well-lit public location — a shopping centre, petrol station, or police station. Avoid secluded or unfamiliar places."
                />
                <SafetyCard
                  icon="💳"
                  title="Avoid Advance Payments"
                  desc="Never send money before physically inspecting the item or receiving a service. Be especially wary of sellers requesting M-Pesa, bank transfers, or gift cards before meeting."
                />
                <SafetyCard
                  icon="🔍"
                  title="Inspect Before Buying"
                  desc="Always test electronics, inspect vehicles, and verify condition before completing payment. Ask for receipts, warranties, or proof of ownership where applicable."
                />
                <SafetyCard
                  icon="🪪"
                  title="Verify Seller Identity"
                  desc="Check the seller's profile — look for verified badges, positive reviews, and listing history. Be cautious of newly created accounts with no reviews or history."
                />
                <SafetyCard
                  icon="📞"
                  title="Use In-Platform Messaging"
                  desc="Keep communication within ImSelling as long as possible. This creates a record and allows us to investigate disputes. Be wary of sellers who immediately push you to WhatsApp or other channels."
                />
                <SafetyCard
                  icon="💰"
                  title="Beware of Too-Good Deals"
                  desc="Deals that seem unrealistically cheap are often scams. If a KES 80,000 phone is listed for KES 5,000, walk away. Trust your instincts — if it feels wrong, it probably is."
                />
                <SafetyCard
                  icon="📝"
                  title="Keep Records"
                  desc="Screenshot conversations, save M-Pesa transaction confirmations, and note the listing URL. This documentation is crucial if you need to file a report or seek legal redress."
                />
                <SafetyCard
                  icon="🔐"
                  title="Protect Your Account"
                  desc="Never share your ImSelling login credentials. Use a strong, unique password. Log out on shared devices. Contact support immediately if you suspect unauthorised access."
                />
              </div>

              {/* Report a Seller */}
              <Section title="🚩 How to Report a Seller">
                <p>
                  If you encounter suspicious activity, fraudulent listings, or a seller who has violated
                  our Terms of Service, please report them immediately. <strong>ImSelling takes all reports
                  seriously and will investigate within 24 hours.</strong>
                </p>
                <div className="tp-report-steps">
                  <div className="tp-report-step">
                    <span className="tp-step-num">1</span>
                    <div>
                      <strong>Open the listing</strong>
                      <p>Go to the ad or seller profile you want to report.</p>
                    </div>
                  </div>
                  <div className="tp-report-step">
                    <span className="tp-step-num">2</span>
                    <div>
                      <strong>Click "Report Seller"</strong>
                      <p>Find the Report button on the listing page or seller profile.</p>
                    </div>
                  </div>
                  <div className="tp-report-step">
                    <span className="tp-step-num">3</span>
                    <div>
                      <strong>Select a reason</strong>
                      <p>Choose from: Fraud / Scam, Counterfeit goods, Harassment, Prohibited item, Fake listing, or Other.</p>
                    </div>
                  </div>
                  <div className="tp-report-step">
                    <span className="tp-step-num">4</span>
                    <div>
                      <strong>Provide details</strong>
                      <p>Include any evidence — screenshots, transaction references, dates, and descriptions.</p>
                    </div>
                  </div>
                  <div className="tp-report-step">
                    <span className="tp-step-num">5</span>
                    <div>
                      <strong>Submit & track</strong>
                      <p>You will receive a confirmation. Our team will review and take action — including suspension, listing removal, or referral to law enforcement.</p>
                    </div>
                  </div>
                </div>
              </Section>

              <Section title="What Happens After You Report?">
                <ul>
                  <li>Reports are reviewed by our Trust & Safety team within <strong>24 hours</strong>.</li>
                  <li>If the report is verified, the listing is removed and the seller's account is suspended or banned immediately.</li>
                  <li>Serious cases involving fraud, financial loss, or criminal activity are referred to the
                      <strong> Directorate of Criminal Investigations (DCI), Kenya</strong> and relevant authorities.</li>
                  <li>Repeat offenders are permanently banned and their details may be shared with law enforcement.</li>
                  <li>False reports made maliciously are also a violation of our Terms and may result in your own account being suspended.</li>
                </ul>
              </Section>

              <Section title="Emergency Contacts">
                <div className="tp-contacts">
                  <div className="tp-contact-row">
                    <span className="tp-contact-icon">🚔</span>
                    <div>
                      <strong>Kenya Police</strong>
                      <span>999 / 112 (Emergency)</span>
                    </div>
                  </div>
                  <div className="tp-contact-row">
                    <span className="tp-contact-icon">🕵️</span>
                    <div>
                      <strong>DCI Kenya (Cybercrime)</strong>
                      <span>0800 722 203 (Toll-free)</span>
                    </div>
                  </div>
                  <div className="tp-contact-row">
                    <span className="tp-contact-icon">📧</span>
                    <div>
                      <strong>ImSelling Support</strong>
                      <span>support@imselling.co.ke</span>
                    </div>
                  </div>
                </div>
              </Section>

              <Section title="Allowed Content on ImSelling">
                <p>
                  ImSelling is a focused marketplace. We deal with the <strong>marketing and promotion of
                  legitimate items and services related to the categories featured on our platform</strong>.
                  This ensures quality, relevance, and safety for all users.
                </p>
                <p>
                  Off-topic listings, spam advertisements, or promotions for services completely unrelated
                  to our featured categories will be removed without notice. Sellers repeatedly posting
                  off-topic content will have their accounts restricted.
                </p>
              </Section>
            </div>
          )}
        </div>

        {/* ── Footer ───────────────────────────────────────────── */}
        <div className="tp-footer">
          <p className="tp-footer-text">
            Last updated: January 2025 &nbsp;·&nbsp; Questions?&nbsp;
            <a href="mailto:support@imselling.co.ke" className="tp-footer-link">support@imselling.co.ke</a>
          </p>
          <button className="tp-accept-btn" onClick={() => { if (onAccept) onAccept(); onClose(); }}>
            I Understand & Accept
          </button>
        </div>

      </div>
    </div>
  );
};

/* ── Small helper components ────────────────────────────────────────────── */
const Section = ({ title, children }) => (
  <div className="tp-block">
    <h2 className="tp-block-title">{title}</h2>
    <div className="tp-block-body">{children}</div>
  </div>
);

const SafetyCard = ({ icon, title, desc }) => (
  <div className="tp-safety-card">
    <span className="tp-safety-icon">{icon}</span>
    <strong className="tp-safety-title">{title}</strong>
    <p className="tp-safety-desc">{desc}</p>
  </div>
);

export default TermsAndPrivacyModal;






























// import React, { useState, useEffect, useRef } from 'react';
// import './TermsAndPrivacyModal.css';

// /**
//  * TermsAndPrivacyModal
//  * Self-contained Terms & Privacy modal for ImSelling.
//  *
//  * DROP-IN USAGE — add 3 lines to your signup/auth component:
//  *
//  *   import TermsAndPrivacyModal from './TermsAndPrivacyModal';
//  *
//  *   const [showTerms, setShowTerms] = useState(false);
//  *
//  *   // Replace your existing Terms link with:
//  *   <span className="terms-link" onClick={() => setShowTerms(true)}>Terms & Privacy</span>
//  *
//  *   // Add the modal anywhere inside your return():
//  *   <TermsAndPrivacyModal isOpen={showTerms} onClose={() => setShowTerms(false)} />
//  */
// const TermsAndPrivacyModal = ({ isOpen, onClose }) => {
//   const [activeSection, setActiveSection] = useState('terms');
//   const contentRef = useRef(null);

//   // Scroll content to top whenever section changes
//   useEffect(() => {
//     if (contentRef.current) contentRef.current.scrollTop = 0;
//   }, [activeSection]);

//   // Close on Escape key
//   useEffect(() => {
//     if (!isOpen) return;
//     const onKey = (e) => { if (e.key === 'Escape') onClose(); };
//     document.addEventListener('keydown', onKey);
//     return () => document.removeEventListener('keydown', onKey);
//   }, [isOpen, onClose]);

//   // Prevent body scroll while open
//   useEffect(() => {
//     if (isOpen) {
//       document.body.style.overflow = 'hidden';
//     } else {
//       document.body.style.overflow = '';
//     }
//     return () => { document.body.style.overflow = ''; };
//   }, [isOpen]);

//   if (!isOpen) return null;

//   return (
//     <div className="tp-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label="Terms and Privacy">
//       <div className="tp-modal" onClick={(e) => e.stopPropagation()}>

//         {/* ── Header ───────────────────────────────────────────── */}
//         <div className="tp-header">
//           <div className="tp-header-brand">
//             <span className="tp-brand-icon">🛒</span>
//             <span className="tp-brand-name">ImSelling</span>
//           </div>
//           <button className="tp-close" onClick={onClose} aria-label="Close">×</button>
//         </div>

//         {/* ── Tab switcher ─────────────────────────────────────── */}
//         <div className="tp-tabs">
//           <button
//             className={`tp-tab ${activeSection === 'terms' ? 'tp-tab-active' : ''}`}
//             onClick={() => setActiveSection('terms')}
//           >
//             📋 Terms of Service
//           </button>
//           <button
//             className={`tp-tab ${activeSection === 'privacy' ? 'tp-tab-active' : ''}`}
//             onClick={() => setActiveSection('privacy')}
//           >
//             🔒 Privacy Policy
//           </button>
//           <button
//             className={`tp-tab ${activeSection === 'safety' ? 'tp-tab-active' : ''}`}
//             onClick={() => setActiveSection('safety')}
//           >
//             🛡️ Safety & Reporting
//           </button>
//         </div>

//         {/* ── Scrollable content ───────────────────────────────── */}
//         <div className="tp-content" ref={contentRef}>

//           {/* ══════════════════ TERMS OF SERVICE ══════════════════ */}
//           {activeSection === 'terms' && (
//             <div className="tp-section">
//               <div className="tp-hero">
//                 <h1 className="tp-hero-title">Terms of Service</h1>
//                 <p className="tp-hero-sub">
//                   Effective Date: January 1, 2025 &nbsp;·&nbsp; ImSelling, Kenya
//                 </p>
//               </div>

//               <div className="tp-notice tp-notice-warn">
//                 <strong>⚠️ Please read carefully.</strong> By creating an account or using ImSelling,
//                 you confirm that you have read, understood, and agreed to these Terms of Service in full.
//                 If you do not agree, please do not use this platform.
//               </div>

//               <Section title="1. About ImSelling">
//                 <p>
//                   ImSelling is an online classifieds and marketplace platform operating in Kenya,
//                   connecting buyers and sellers of goods and services. We act solely as an
//                   intermediary platform — we do <strong>not</strong> buy, sell, own, or guarantee
//                   any item listed on the platform.
//                 </p>
//                 <p>
//                   ImSelling is available to Kenyan residents and businesses aged 18 years and above,
//                   or minors acting under verified parental/guardian supervision.
//                 </p>
//               </Section>

//               <Section title="2. Eligibility & Account Registration">
//                 <ul>
//                   <li>You must provide accurate, truthful, and complete registration information.</li>
//                   <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
//                   <li>One person or business entity may hold only one active account unless explicitly authorised by ImSelling.</li>
//                   <li>Accounts created using bots, scripts, or automated tools are strictly prohibited.</li>
//                   <li>We reserve the right to suspend or permanently ban accounts at our sole discretion for violations of these Terms.</li>
//                 </ul>
//               </Section>

//               <Section title="3. Listings & Permitted Content">
//                 <p>
//                   ImSelling is designed for the <strong>marketing and sale of legitimate physical and digital
//                   goods and services</strong> that are consistent with the categories featured on our platform.
//                   This includes but is not limited to electronics, clothing, furniture, vehicles, real estate,
//                   services, and locally manufactured or imported goods.
//                 </p>
//                 <p><strong>You may NOT list or advertise:</strong></p>
//                 <ul>
//                   <li>Counterfeit, pirated, or stolen goods</li>
//                   <li>Illegal substances, narcotics, or controlled goods without proper licencing</li>
//                   <li>Weapons, firearms, ammunition, or explosives</li>
//                   <li>Endangered animals or animal parts prohibited under Kenyan law or CITES</li>
//                   <li>Adult/pornographic content of any kind</li>
//                   <li>Human trafficking, exploitation, or services of a sexual nature</li>
//                   <li>Financial scam products, pyramid schemes, or Ponzi investments</li>
//                   <li>Fake currency, forged documents, or fraudulent financial instruments</li>
//                   <li>Items that are off-topic or unrelated to the categories featured on our platform</li>
//                   <li>Services that impersonate another person, business, or government entity</li>
//                   <li>Any item or service whose sale is prohibited under Kenyan law</li>
//                 </ul>
//                 <p>
//                   Listings that violate these rules will be removed without notice, and the account may be
//                   permanently banned. ImSelling cooperates fully with Kenyan law enforcement agencies.
//                 </p>
//               </Section>

//               <Section title="4. Seller Responsibilities">
//                 <ul>
//                   <li>Sellers must accurately describe items — price, condition, location, and images must be truthful.</li>
//                   <li>Sellers must honour agreed prices and complete transactions in good faith.</li>
//                   <li>Sellers must not engage in bait-and-switch tactics or price manipulation after initial contact.</li>
//                   <li>Sellers are solely responsible for the quality, legality, and delivery of their goods/services.</li>
//                   <li>Sellers must respond to buyer enquiries within a reasonable timeframe.</li>
//                   <li>Commercial sellers operating as businesses must comply with Kenyan consumer protection laws,
//                       including the Consumer Protection Act, 2012.</li>
//                 </ul>
//               </Section>

//               <Section title="5. Buyer Responsibilities">
//                 <ul>
//                   <li>Buyers must conduct due diligence before making any purchase or payment.</li>
//                   <li>Buyers should inspect goods thoroughly before completing a transaction.</li>
//                   <li>Buyers must not file false reports or make fraudulent claims against sellers.</li>
//                   <li>Buyers acknowledge that ImSelling does not guarantee the quality, safety,
//                       or legality of items listed by third-party sellers.</li>
//                 </ul>
//               </Section>

//               <Section title="6. Fees & Premium Plans">
//                 <ul>
//                   <li>Posting a Free-tier ad is completely free.</li>
//                   <li>Premium plans (Basic, Pro, Business) require payment via M-Pesa before activation.</li>
//                   <li>All fees are non-refundable once a plan is activated and an ad is live, unless otherwise stated.</li>
//                   <li>ImSelling reserves the right to change pricing at any time with reasonable notice.</li>
//                   <li>Promotional slots granted by ImSelling are non-transferable and expire as indicated.</li>
//                 </ul>
//               </Section>

//               <Section title="7. Prohibited Conduct">
//                 <p>The following are strictly forbidden on ImSelling:</p>
//                 <ul>
//                   <li><strong>Impersonation:</strong> Pretending to be another seller, buyer, or ImSelling staff.</li>
//                   <li><strong>Spam:</strong> Posting duplicate or irrelevant listings; unsolicited bulk messaging.</li>
//                   <li><strong>Manipulation:</strong> Artificially inflating listing views, reviews, or engagement.</li>
//                   <li><strong>Scraping:</strong> Automated data extraction from the platform without written permission.</li>
//                   <li><strong>Harassment:</strong> Abusive, threatening, or discriminatory communication to any user.</li>
//                   <li><strong>Fraud:</strong> Any deceptive practice intended to mislead buyers or sellers.</li>
//                   <li><strong>Account sharing:</strong> Sharing login credentials with third parties.</li>
//                 </ul>
//               </Section>

//               <Section title="8. Limitation of Liability">
//                 <div className="tp-notice tp-notice-info">
//                   <strong>ImSelling is a marketplace platform, not a party to any transaction.</strong>
//                 </div>
//                 <ul>
//                   <li>ImSelling is <strong>not liable</strong> for any loss, damage, injury, or financial harm
//                       arising from transactions between buyers and sellers on this platform.</li>
//                   <li>We are <strong>not liable</strong> for misrepresentation, fraud, or misconduct by any user.</li>
//                   <li>We are <strong>not liable</strong> for loss of data, business interruption, or technical errors.</li>
//                   <li>We are <strong>not liable</strong> for any advance payments made by buyers without conducting
//                       proper due diligence.</li>
//                   <li>While we strive to ensure genuine businesses are listed, we <strong>cannot guarantee</strong> the
//                       authenticity of every seller. Users transact at their own risk.</li>
//                   <li>ImSelling's total liability, in any event, shall not exceed the amount paid by the user
//                       for the specific plan or service giving rise to the claim.</li>
//                 </ul>
//               </Section>

//               <Section title="9. Personalisation & Account Banning">
//                 <div className="tp-notice tp-notice-danger">
//                   <strong>⚠️ Zero Tolerance:</strong> Impersonation and account manipulation will result in
//                   permanent banning and potential legal action.
//                 </div>
//                 <ul>
//                   <li>Personalisation of accounts to mislead users — including false business names, fake
//                       verification badges, or impersonating well-known brands — is strictly prohibited.</li>
//                   <li>Accounts found engaging in personalisation fraud will be <strong>permanently banned</strong>
//                       without refund of any fees paid.</li>
//                   <li>ImSelling reserves the right to pursue <strong>civil and criminal legal action</strong>
//                       under the Computer Misuse and Cybercrimes Act, 2018 and other applicable Kenyan laws.</li>
//                   <li>Banned users may not create new accounts. Circumventing a ban is itself a violation
//                       subject to further legal action.</li>
//                 </ul>
//               </Section>

//               <Section title="10. Intellectual Property">
//                 <ul>
//                   <li>All content on ImSelling (logo, branding, software, UI) is owned by ImSelling
//                       and may not be copied, reproduced, or used without written permission.</li>
//                   <li>By posting listings, you grant ImSelling a non-exclusive, royalty-free licence to
//                       display your content on the platform for the purposes of operating the marketplace.</li>
//                   <li>You must have the rights to all content (images, text, videos) you upload. Uploading
//                       copyrighted material without permission is prohibited.</li>
//                 </ul>
//               </Section>

//               <Section title="11. Termination">
//                 <p>
//                   ImSelling may suspend or terminate your account at any time, with or without notice,
//                   for conduct that we believe violates these Terms or is harmful to other users, third parties,
//                   or the business interests of ImSelling. Upon termination, your right to use the
//                   platform immediately ceases.
//                 </p>
//               </Section>

//               <Section title="12. Governing Law">
//                 <p>
//                   These Terms are governed by and construed in accordance with the laws of the
//                   Republic of Kenya. Any disputes arising from these Terms or your use of the platform
//                   shall be subject to the exclusive jurisdiction of the Kenyan courts.
//                 </p>
//               </Section>

//               <Section title="13. Changes to These Terms">
//                 <p>
//                   ImSelling reserves the right to update these Terms at any time. Continued use of
//                   the platform after changes are posted constitutes your acceptance of the revised Terms.
//                   We will notify users of material changes via email or platform notification.
//                 </p>
//               </Section>
//             </div>
//           )}

//           {/* ══════════════════ PRIVACY POLICY ══════════════════ */}
//           {activeSection === 'privacy' && (
//             <div className="tp-section">
//               <div className="tp-hero">
//                 <h1 className="tp-hero-title">Privacy Policy</h1>
//                 <p className="tp-hero-sub">
//                   Effective Date: January 1, 2025 &nbsp;·&nbsp; ImSelling, Kenya
//                 </p>
//               </div>

//               <div className="tp-notice tp-notice-info">
//                 Your privacy matters to us. This Policy explains what data we collect, why we collect it,
//                 and how we protect it.
//               </div>

//               <Section title="1. Information We Collect">
//                 <p><strong>Registration data:</strong> Name, email address, phone number, password (encrypted).</p>
//                 <p><strong>Listing data:</strong> Photos, videos, item descriptions, prices, and location data you provide when posting ads.</p>
//                 <p><strong>Transaction data:</strong> M-Pesa payment references, plan subscriptions, and promo slot usage.</p>
//                 <p><strong>Usage data:</strong> Pages visited, search queries, click behaviour, device type, IP address, and browser information.</p>
//                 <p><strong>Communication data:</strong> Messages exchanged between buyers and sellers on the platform.</p>
//                 <p><strong>Verification data:</strong> ID documents submitted for seller verification (stored securely, never shared publicly).</p>
//               </Section>

//               <Section title="2. How We Use Your Data">
//                 <ul>
//                   <li>To create and manage your account.</li>
//                   <li>To display your listings to potential buyers.</li>
//                   <li>To process payments and activate premium plans.</li>
//                   <li>To send transactional notifications (account activity, payment confirmations).</li>
//                   <li>To verify seller identities and improve platform safety.</li>
//                   <li>To detect, prevent, and respond to fraud, abuse, or illegal activity.</li>
//                   <li>To improve platform features, performance, and user experience.</li>
//                   <li>To comply with our legal obligations under Kenyan law.</li>
//                 </ul>
//               </Section>

//               <Section title="3. Data Sharing">
//                 <p>We do <strong>not</strong> sell your personal data. We may share data with:</p>
//                 <ul>
//                   <li><strong>Payment processors:</strong> Safaricom M-Pesa for payment processing only.</li>
//                   <li><strong>Law enforcement:</strong> When required by Kenyan law or a valid court order.</li>
//                   <li><strong>Service providers:</strong> Trusted third parties who help operate the platform
//                       (e.g. cloud hosting, analytics) — bound by strict data processing agreements.</li>
//                   <li><strong>Other users:</strong> Your public listing information (name, listing details,
//                       general location) is visible to all platform users.</li>
//                 </ul>
//               </Section>

//               <Section title="4. Data Security">
//                 <ul>
//                   <li>Passwords are stored using strong encryption (hashed — never in plain text).</li>
//                   <li>All data is transmitted over encrypted HTTPS connections.</li>
//                   <li>ID verification documents are stored in restricted-access secure storage.</li>
//                   <li>We regularly review our security practices and update them as needed.</li>
//                   <li>Despite best efforts, no system is 100% secure. Users are encouraged to use
//                       strong passwords and report suspicious account activity immediately.</li>
//                 </ul>
//               </Section>

//               <Section title="5. Data Retention">
//                 <p>
//                   We retain your account data for as long as your account is active. If you delete your
//                   account, we will delete or anonymise your personal data within 30 days, except where
//                   retention is required by Kenyan law (e.g. financial records, fraud investigations).
//                 </p>
//               </Section>

//               <Section title="6. Your Rights">
//                 <p>Under applicable Kenyan data protection law (Data Protection Act, 2019), you have the right to:</p>
//                 <ul>
//                   <li>Access the personal data we hold about you.</li>
//                   <li>Correct inaccurate or incomplete data.</li>
//                   <li>Request deletion of your data (subject to legal retention requirements).</li>
//                   <li>Object to processing of your data for direct marketing.</li>
//                   <li>Lodge a complaint with the Office of the Data Protection Commissioner (ODPC) of Kenya.</li>
//                 </ul>
//                 <p>To exercise your rights, contact us at: <strong>support@imselling.co.ke</strong></p>
//               </Section>

//               <Section title="7. Cookies & Tracking">
//                 <p>
//                   ImSelling uses cookies and similar tracking technologies to maintain your session,
//                   remember preferences, and analyse usage patterns. You may disable cookies in your browser
//                   settings, but this may affect platform functionality.
//                 </p>
//               </Section>

//               <Section title="8. Children's Privacy">
//                 <p>
//                   ImSelling is not intended for users under the age of 18. We do not knowingly collect
//                   personal data from children. If we become aware that a child has registered without
//                   parental consent, we will delete the account immediately.
//                 </p>
//               </Section>

//               <Section title="9. Changes to This Policy">
//                 <p>
//                   We may update this Privacy Policy periodically. Significant changes will be communicated
//                   via email or platform notification. Continued use of the platform after changes
//                   constitutes acceptance of the updated Policy.
//                 </p>
//               </Section>
//             </div>
//           )}

//           {/* ══════════════════ SAFETY & REPORTING ══════════════════ */}
//           {activeSection === 'safety' && (
//             <div className="tp-section">
//               <div className="tp-hero">
//                 <h1 className="tp-hero-title">Safety & Reporting</h1>
//                 <p className="tp-hero-sub">
//                   Staying safe on ImSelling — tips, guidelines, and how to report
//                 </p>
//               </div>

//               <div className="tp-notice tp-notice-success">
//                 <strong>🛡️ Our commitment:</strong> ImSelling is committed to maintaining a safe,
//                 trustworthy marketplace. We will take immediate action on all verified reports of
//                 suspicious or fraudulent activity.
//               </div>

//               {/* Safety Tips */}
//               <div className="tp-safety-grid">
//                 <SafetyCard
//                   icon="🤝"
//                   title="Meet in Public Places"
//                   desc="Always arrange to meet buyers or sellers in a busy, well-lit public location — a shopping centre, petrol station, or police station. Avoid secluded or unfamiliar places."
//                 />
//                 <SafetyCard
//                   icon="💳"
//                   title="Avoid Advance Payments"
//                   desc="Never send money before physically inspecting the item or receiving a service. Be especially wary of sellers requesting M-Pesa, bank transfers, or gift cards before meeting."
//                 />
//                 <SafetyCard
//                   icon="🔍"
//                   title="Inspect Before Buying"
//                   desc="Always test electronics, inspect vehicles, and verify condition before completing payment. Ask for receipts, warranties, or proof of ownership where applicable."
//                 />
//                 <SafetyCard
//                   icon="🪪"
//                   title="Verify Seller Identity"
//                   desc="Check the seller's profile — look for verified badges, positive reviews, and listing history. Be cautious of newly created accounts with no reviews or history."
//                 />
//                 <SafetyCard
//                   icon="📞"
//                   title="Use In-Platform Messaging"
//                   desc="Keep communication within ImSelling as long as possible. This creates a record and allows us to investigate disputes. Be wary of sellers who immediately push you to WhatsApp or other channels."
//                 />
//                 <SafetyCard
//                   icon="💰"
//                   title="Beware of Too-Good Deals"
//                   desc="Deals that seem unrealistically cheap are often scams. If a KES 80,000 phone is listed for KES 5,000, walk away. Trust your instincts — if it feels wrong, it probably is."
//                 />
//                 <SafetyCard
//                   icon="📝"
//                   title="Keep Records"
//                   desc="Screenshot conversations, save M-Pesa transaction confirmations, and note the listing URL. This documentation is crucial if you need to file a report or seek legal redress."
//                 />
//                 <SafetyCard
//                   icon="🔐"
//                   title="Protect Your Account"
//                   desc="Never share your ImSelling login credentials. Use a strong, unique password. Log out on shared devices. Contact support immediately if you suspect unauthorised access."
//                 />
//               </div>

//               {/* Report a Seller */}
//               <Section title="🚩 How to Report a Seller">
//                 <p>
//                   If you encounter suspicious activity, fraudulent listings, or a seller who has violated
//                   our Terms of Service, please report them immediately. <strong>ImSelling takes all reports
//                   seriously and will investigate within 24 hours.</strong>
//                 </p>
//                 <div className="tp-report-steps">
//                   <div className="tp-report-step">
//                     <span className="tp-step-num">1</span>
//                     <div>
//                       <strong>Open the listing</strong>
//                       <p>Go to the ad or seller profile you want to report.</p>
//                     </div>
//                   </div>
//                   <div className="tp-report-step">
//                     <span className="tp-step-num">2</span>
//                     <div>
//                       <strong>Click "Report Seller"</strong>
//                       <p>Find the Report button on the listing page or seller profile.</p>
//                     </div>
//                   </div>
//                   <div className="tp-report-step">
//                     <span className="tp-step-num">3</span>
//                     <div>
//                       <strong>Select a reason</strong>
//                       <p>Choose from: Fraud / Scam, Counterfeit goods, Harassment, Prohibited item, Fake listing, or Other.</p>
//                     </div>
//                   </div>
//                   <div className="tp-report-step">
//                     <span className="tp-step-num">4</span>
//                     <div>
//                       <strong>Provide details</strong>
//                       <p>Include any evidence — screenshots, transaction references, dates, and descriptions.</p>
//                     </div>
//                   </div>
//                   <div className="tp-report-step">
//                     <span className="tp-step-num">5</span>
//                     <div>
//                       <strong>Submit & track</strong>
//                       <p>You will receive a confirmation. Our team will review and take action — including suspension, listing removal, or referral to law enforcement.</p>
//                     </div>
//                   </div>
//                 </div>
//               </Section>

//               <Section title="What Happens After You Report?">
//                 <ul>
//                   <li>Reports are reviewed by our Trust & Safety team within <strong>24 hours</strong>.</li>
//                   <li>If the report is verified, the listing is removed and the seller's account is suspended or banned immediately.</li>
//                   <li>Serious cases involving fraud, financial loss, or criminal activity are referred to the
//                       <strong> Directorate of Criminal Investigations (DCI), Kenya</strong> and relevant authorities.</li>
//                   <li>Repeat offenders are permanently banned and their details may be shared with law enforcement.</li>
//                   <li>False reports made maliciously are also a violation of our Terms and may result in your own account being suspended.</li>
//                 </ul>
//               </Section>

//               <Section title="Emergency Contacts">
//                 <div className="tp-contacts">
//                   <div className="tp-contact-row">
//                     <span className="tp-contact-icon">🚔</span>
//                     <div>
//                       <strong>Kenya Police</strong>
//                       <span>999 / 112 (Emergency)</span>
//                     </div>
//                   </div>
//                   <div className="tp-contact-row">
//                     <span className="tp-contact-icon">🕵️</span>
//                     <div>
//                       <strong>DCI Kenya (Cybercrime)</strong>
//                       <span>0800 722 203 (Toll-free)</span>
//                     </div>
//                   </div>
//                   <div className="tp-contact-row">
//                     <span className="tp-contact-icon">📧</span>
//                     <div>
//                       <strong>ImSelling Support</strong>
//                       <span>support@imselling.co.ke</span>
//                     </div>
//                   </div>
//                 </div>
//               </Section>

//               <Section title="Allowed Content on ImSelling">
//                 <p>
//                   ImSelling is a focused marketplace. We deal with the <strong>marketing and promotion of
//                   legitimate items and services related to the categories featured on our platform</strong>.
//                   This ensures quality, relevance, and safety for all users.
//                 </p>
//                 <p>
//                   Off-topic listings, spam advertisements, or promotions for services completely unrelated
//                   to our featured categories will be removed without notice. Sellers repeatedly posting
//                   off-topic content will have their accounts restricted.
//                 </p>
//               </Section>
//             </div>
//           )}
//         </div>

//         {/* ── Footer ───────────────────────────────────────────── */}
//         <div className="tp-footer">
//           <p className="tp-footer-text">
//             Last updated: January 2025 &nbsp;·&nbsp; Questions?&nbsp;
//             <a href="mailto:support@imselling.co.ke" className="tp-footer-link">support@imselling.co.ke</a>
//           </p>
//           <button className="tp-accept-btn" onClick={onClose}>
//             I Understand & Accept
//           </button>
//         </div>

//       </div>
//     </div>
//   );
// };

// /* ── Small helper components ────────────────────────────────────────────── */
// const Section = ({ title, children }) => (
//   <div className="tp-block">
//     <h2 className="tp-block-title">{title}</h2>
//     <div className="tp-block-body">{children}</div>
//   </div>
// );

// const SafetyCard = ({ icon, title, desc }) => (
//   <div className="tp-safety-card">
//     <span className="tp-safety-icon">{icon}</span>
//     <strong className="tp-safety-title">{title}</strong>
//     <p className="tp-safety-desc">{desc}</p>
//   </div>
// );

// export default TermsAndPrivacyModal;